import {
  IconArrowLeft,
  IconArrowRight,
  IconChecks,
  IconCurrencyDollar,
  IconDownload,
  IconReceipt2,
  IconUsers,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { EnhancedMetricCard } from '@/components/enhanced-metric-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '@/components/ui/table';
import { Tabs, TabsContent } from '@/components/ui/tabs';

const paymentOptimizationData = [
  {
    paymentType: 'Foreign Exchange',
    spend: '$53,651.00',
    transactionCount: 6,
    supplierCount: 3,
    percentSuppliers: '1%',
    percentSpend: '37%',
    percentRevGeneratingSpend: '6%',
    percentRevGeneratingTransactions: '20%',
  },
  {
    paymentType: 'Wire Transfer',
    spend: '$39,888,434.00',
    transactionCount: 5,
    supplierCount: 179,
    percentSuppliers: '17%',
    percentSpend: '37%',
    percentRevGeneratingSpend: '41%',
    percentRevGeneratingTransactions: '20%',
  },
  {
    paymentType: 'Check',
    spend: '$65,343,207.00',
    transactionCount: 18,
    supplierCount: 816,
    percentSuppliers: '80%',
    percentSpend: '62%',
    percentRevGeneratingSpend: '0%',
    percentRevGeneratingTransactions: '0%',
  },
  {
    paymentType: 'Card',
    spend: '$1,243,013.00',
    transactionCount: 1,
    supplierCount: 22,
    percentSuppliers: '2%',
    percentSpend: '1%',
    percentRevGeneratingSpend: '1%',
    percentRevGeneratingTransactions: '4%',
  },
];

const paymentMixData = [
  { name: 'Check', value: 65343207, color: '#06b6d4' },
  { name: 'Electronic', value: 39332435, color: '#4338ca' },
  { name: 'Virtual Card', value: 1243013, color: '#10b981' },
];

const supplierData = [
  {
    vendorId: 'QUICK ENTERPRISES',
    taxId: '07-0867',
    type: 'Strategic',
    transactionCount: 178,
    totalSpend: '$2,573,988.00',
  },
  {
    vendorId: 'STARSHINE',
    taxId: '07-0863',
    type: 'Strategic',
    transactionCount: 184,
    totalSpend: '$2,519,847.00',
  },
  {
    vendorId: 'US FOOD',
    taxId: '07-0864',
    type: 'Strategic',
    transactionCount: 248,
    totalSpend: '$2,373,816.00',
  },
  {
    vendorId: 'PORTER AMERICA',
    taxId: '07-0874',
    type: 'Strategic',
    transactionCount: 81,
    totalSpend: '$2,219,819.00',
  },
  {
    vendorId: 'HEVEDA DEPARTMENT OF TAXATION',
    taxId: '07-0841',
    type: 'Network Name Match',
    transactionCount: 80,
    totalSpend: '$2,186,547.00',
  },
  {
    vendorId: 'HEALTH PLAN OF NEVADA',
    taxId: '07-4323',
    type: 'Network Name Match',
    transactionCount: 97,
    totalSpend: '$2,002,834.00',
  },
  {
    vendorId: 'US FOOD',
    taxId: '07-4862',
    type: 'Strategic',
    transactionCount: 240,
    totalSpend: '$1,949,383.00',
  },
  {
    vendorId: 'COMMODORE LODGING',
    taxId: '07-4079',
    type: 'Strategic',
    transactionCount: 104,
    totalSpend: '$1,792,358.00',
  },
  {
    vendorId: 'SILVER STATE SERVICES',
    taxId: '07-6505',
    type: 'Network Name Match',
    transactionCount: 140,
    totalSpend: '$1,790,524.00',
  },
  {
    vendorId: 'BOOKING.COM',
    taxId: '07-5739',
    type: 'Network Name Match',
    transactionCount: 75,
    totalSpend: '$1,716,790.00',
  },
  {
    vendorId: 'NEVADA GAMING COMMISSION',
    taxId: '07-5943',
    type: 'Strategic',
    transactionCount: 63,
    totalSpend: '$1,684,134.00',
  },
  {
    vendorId: 'EXPRESS',
    taxId: '07-5939',
    type: 'Strategic',
    transactionCount: 74,
    totalSpend: '$1,942,389.00',
  },
];

interface ProspectViewCurrentComponentProps {
  companyName: string;
  onComplete?: () => void;
  onPrevious?: () => void;
}

export function ProspectViewCurrentComponent({
  companyName,
  onComplete,
  onPrevious,
}: ProspectViewCurrentComponentProps) {
  const [animateMix, setAnimateMix] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimateMix(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Current State Analysis</h1>
          <p className="text-sm text-muted-foreground">
            Current payment metrics and supplier analysis for {companyName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <IconDownload className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <Tabs defaultValue="solution" className="space-y-6">
        <TabsContent value="solution" className="space-y-6">
          <div>
            <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2 @4xl:grid-cols-4">
              <EnhancedMetricCard
                title="Total Spend"
                value="$105.7M"
                subtitle="Daily avg: $150K"
                description="85% of spend is addressable for optimization"
                icon={IconCurrencyDollar}
                gradient="from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-950/10"
                accentClass="bg-indigo-500/20 text-indigo-600"
                variant="bottom-progress"
                progress={{ value: 85, target: 100 }}
              />
              <EnhancedMetricCard
                title="Transactions"
                value={26690}
                subtitle="Processed transactions"
                description="58% of all transactions processed"
                icon={IconReceipt2}
                gradient="from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-950/10"
                accentClass="bg-emerald-500/20 text-emerald-600"
                variant="bottom-progress"
                progress={{ value: 58 }}
              />
              <EnhancedMetricCard
                title="Beneficiaries"
                value={1020}
                subtitle="Beneficiaries engaged"
                description="89% retention rate (of all beneficiaries from last year)"
                icon={IconUsers}
                gradient="from-purple-50 to-white dark:from-purple-950/30 dark:to-slate-950/10"
                accentClass="bg-pink-500/20 text-pink-600"
                variant="bottom-progress"
                progress={{ value: 89 }}
              />
              <EnhancedMetricCard
                title="Check Payments"
                value="68%"
                subtitle="Traditional payment method usage"
                description="68% transactions paid by check"
                icon={IconChecks}
                gradient="from-indigo-50 to-white dark:from-indigo-950/30 dark:to-slate-950/10"
                accentClass="bg-indigo-500/20 text-indigo-700"
                variant="bottom-progress"
                progress={{ value: 68, target: 100 }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  Pre-SteerFi Optimization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table caption="Pre-SteerFi Optimization by payment type">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Payment Type</TableHead>
                        <TableHead>Spend</TableHead>
                        <TableHead>Transaction Count</TableHead>
                        <TableHead>Supplier Count</TableHead>
                        <TableHead>% of Suppliers</TableHead>
                        <TableHead>% of Spend</TableHead>
                        <TableHead>% of Rev. Generating Spend</TableHead>
                        <TableHead>% of Rev. Generating Transactions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentOptimizationData.map((row) => (
                        <TableRow key={row.paymentType}>
                          <TableRowHeader>
                            <Badge variant={row.paymentType === 'Check' ? 'default' : 'secondary'}>
                              {row.paymentType}
                            </Badge>
                          </TableRowHeader>
                          <TableCell>{row.spend}</TableCell>
                          <TableCell>{row.transactionCount}</TableCell>
                          <TableCell>{row.supplierCount}</TableCell>
                          <TableCell>{row.percentSuppliers}</TableCell>
                          <TableCell>{row.percentSpend}</TableCell>
                          <TableCell>{row.percentRevGeneratingSpend}</TableCell>
                          <TableCell>{row.percentRevGeneratingTransactions}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="border-t-2">
                        <TableCell>
                          <Badge>Total</Badge>
                        </TableCell>
                        <TableCell>$105,718,395.00</TableCell>
                        <TableCell>25,682</TableCell>
                        <TableCell>3,048</TableCell>
                        <TableCell>100%</TableCell>
                        <TableCell>100%</TableCell>
                        <TableCell>48%</TableCell>
                        <TableCell>24%</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle as="h2" className="flex items-center gap-2">
                  Payment Mix Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="space-y-3">
                    {paymentMixData.map((item) => (
                      <div key={item.name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{item.name}</span>
                          <span>${item.value.toLocaleString()}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              backgroundColor: item.color,
                              width: animateMix
                                ? `${(item.value / Math.max(...paymentMixData.map((d) => d.value))) * 100}%`
                                : '0%',
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle as="h2">Top 15 Suppliers By Spend</CardTitle>
              </CardHeader>
              <CardContent>
                <Table caption="Top 15 suppliers by spend">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor ID</TableHead>
                      <TableHead>Tax ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Transaction Count</TableHead>
                      <TableHead>Total Spend</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supplierData.slice(0, 8).map((supplier) => (
                      <TableRow key={`spend-${supplier.vendorId}-${supplier.taxId}`}>
                        <TableRowHeader className="font-medium">{supplier.vendorId}</TableRowHeader>
                        <TableCell>{supplier.taxId}</TableCell>
                        <TableCell>
                          <Badge variant={supplier.type === 'Strategic' ? 'default' : 'secondary'}>
                            {supplier.type}
                          </Badge>
                        </TableCell>
                        <TableCell>{supplier.transactionCount}</TableCell>
                        <TableCell>{supplier.totalSpend}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle as="h2">Top 15 Suppliers By Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <Table caption="Top 15 suppliers by transactions">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor ID</TableHead>
                      <TableHead>Tax ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Transaction Count</TableHead>
                      <TableHead>Total Spend</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supplierData.slice(0, 8).map((supplier) => (
                      <TableRow key={`transactions-${supplier.vendorId}-${supplier.taxId}`}>
                        <TableRowHeader className="font-medium">{supplier.vendorId}</TableRowHeader>
                        <TableCell>{supplier.taxId}</TableCell>
                        <TableCell>
                          <Badge variant={supplier.type === 'Strategic' ? 'default' : 'secondary'}>
                            {supplier.type}
                          </Badge>
                        </TableCell>
                        <TableCell>{supplier.transactionCount}</TableCell>
                        <TableCell>{supplier.totalSpend}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {(onPrevious || onComplete) && (
            <Card>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    {onPrevious && (
                      <Button variant="outline" onClick={onPrevious} className="gap-2">
                        <IconArrowLeft className="h-4 w-4" />
                        Previous: Setup
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <h2 className="font-semibold leading-tight">Ready for next step?</h2>
                      <p className="text-sm text-muted-foreground">
                        Proceed to competitive analysis and industry benchmarks
                      </p>
                    </div>
                    {onComplete && (
                      <Button onClick={onComplete} className="gap-2">
                        Next: Other People's Approach
                        <IconArrowRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {['account-setup', 'solution-design', 'pricing', 'preview'].map((tab) => (
          <TabsContent key={tab} value={tab}>
            <Card>
              <CardHeader>
                <CardTitle as="h2">
                  {tab
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  This section is coming soon. The {tab.replace('-', ' ')} interface will be
                  available here.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
